import React, { useRef, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Radio,
  Stack,
  Typography,
} from "@mui/material";

import {
  CalendarMonth as CalendarMonthIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
} from "@mui/icons-material";

import {
  textColorHandler,
  getTerminalCallArrivalStateContainerIds,
} from "../Helpers.tsx";

import { Container } from "../../types/Container.ts";
import { TerminalCall } from "../../types/TerminalCall.ts";
import { normalizeId } from "../utils/normalize.tsx";
import SimpleShifterArrowConnector from "../partial/SimpleShifterArrowConnector.tsx";
import {
  ContainerPlanningSymbol,
  ContainerPlanningSymbolType,
} from "./ContainerPlanningSymbols.tsx";

import TerminalContainerPlannerButtons, {
  DOCUMENT_FIT_CONTAIN,
  DOCUMENT_FIT_STRETCH,
  DOCUMENT_ORIENTATION_LANDSCAPE,
  DocumentFitMode,
} from "./TerminalContainerPlannerButtons.tsx";

const NO_CONTAINER_COLOR = "#FFFFFF";
const DEFAULT_CONTAINER_COLOR = "#FFE4C4";
const LOADING_CONTAINER_COLOR = "loading_color";
const UNLOADING_CONTAINER_COLOR = "unloading_color";
const PLAN_TYPE_LOADING = "loadingTerminalCall";
const PLAN_TYPE_UNLOADING = "unloadingTerminalCall";
const DOCKING_SIDE_STARBOARD = "starboard";
const PLAN_TYPE_SHIFTERS = "shifters";
const PLAN_TYPE_LOADING_ONLY = "loading_only";

type GridContentSetting =
  | "containerNumber"
  | "weight"
  | "destinationAbbreviation"
  | "sourceAbbreviation";
type GridSymbolSetting = ContainerPlanningSymbolType;
type GridColorPreference = "full" | "border" | "blackWhite" | "inverted";
type GridLayerRange = "all" | "filled";

const AVAILABLE_GRID_SYMBOLS: Record<GridSymbolSetting, boolean> = {
  empty: true,
  adn: false,
  reefer: false,
  heavy: false,
};

interface TerminalCallGridProps {
  containers: Container[];
  selectedTerminalCall: TerminalCall;
  selectedVessel?: any;
  planType?: string;
  containerColorType?: string;
  dockingSide?: string;
  containerTypes?: any;
}

const getEntityIds = (entity: any): string[] => {
  if (!entity) {
    return [];
  }

  if (typeof entity === "string" || typeof entity === "number") {
    const raw = String(entity);

    return Array.from(new Set([raw, normalizeId(raw)].filter(Boolean)));
  }

  const rawId = entity["@id"] ?? entity.id ?? null;

  if (!rawId) {
    return [];
  }

  const raw = String(rawId);

  return Array.from(new Set([raw, normalizeId(raw)].filter(Boolean)));
};

const hasSameEntityId = (left: any, right: any) => {
  const leftIds = getEntityIds(left);
  const rightIds = getEntityIds(right);

  return leftIds.some((leftId) => rightIds.includes(leftId));
};

const filterContainersByRefs = (
  containers: Container[] = [],
  refs: any[] = []
) => {
  if (!Array.isArray(refs) || refs.length === 0) {
    return [];
  }

  return containers.filter((container: any) =>
    refs.some((ref: any) => hasSameEntityId(container, ref))
  );
};

const TerminalCallContainerPlanning: React.FC<TerminalCallGridProps> = ({
  containers,
  selectedTerminalCall,
  selectedVessel,
  planType = PLAN_TYPE_LOADING,
  containerColorType = LOADING_CONTAINER_COLOR,
  dockingSide = DOCKING_SIDE_STARBOARD,
  containerTypes,
}) => {
  const { t } = useTranslation();
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const [gridContent, setGridContent] = useState<
    Record<GridContentSetting, boolean>
  >({
    containerNumber: true,
    weight: false,
    destinationAbbreviation: true,
    sourceAbbreviation: false,
  });
  const [gridSymbols, setGridSymbols] = useState<
    Record<GridSymbolSetting, boolean>
  >({ empty: true, adn: false, reefer: false, heavy: false });
  const [colorPreference, setColorPreference] =
    useState<GridColorPreference>("full");
  const [layerRange, setLayerRange] = useState<GridLayerRange>("all");
  const [pdfFitMode, setPdfFitMode] =
    useState<DocumentFitMode>(DOCUMENT_FIT_STRETCH);
  const gridSymbolOptions: Array<{
    key: GridSymbolSetting;
    label: string;
  }> = [
    { key: "empty", label: t("emptyContainer") },
    { key: "adn", label: t("adnContainer") },
    { key: "reefer", label: t("reefer") },
    { key: "heavy", label: t("heavyContainer") },
  ];

  const toggleGridContent = (key: GridContentSetting) => {
    setGridContent((current) => {
      const selectedCount = Object.values(current).filter(Boolean).length;
      if (!current[key] && selectedCount >= 2) return current;
      return { ...current, [key]: !current[key] };
    });
  };

  const toggleGridSymbol = (key: GridSymbolSetting) => {
    if (!AVAILABLE_GRID_SYMBOLS[key]) return;
    setGridSymbols((current) => ({ ...current, [key]: !current[key] }));
  };

  const fileName = () => {
    return `${selectedVessel?.name}_${selectedTerminalCall?.terminal?.name}_${
      planType === PLAN_TYPE_LOADING || planType === PLAN_TYPE_LOADING_ONLY
        ? t("loading")
        : t("unloading")
    }_${dayjs(selectedTerminalCall?.planned).format("DD/MM/YYYY HH:mm")}`;
  };

  const isUnloadingPlan = planType === PLAN_TYPE_UNLOADING;

  const planningTypeLabel = useMemo(() => {
    if (planType === PLAN_TYPE_UNLOADING) {
      return t("unloading");
    }

    if (planType === PLAN_TYPE_SHIFTERS) {
      return t("shifters");
    }

    return t("loading");
  }, [planType, t]);

  const countTitle = useMemo(() => {
    if (planType === PLAN_TYPE_UNLOADING) {
      return t("unload");
    }

    if (planType === PLAN_TYPE_SHIFTERS) {
      return t("shift");
    }

    return t("load");
  }, [planType, t]);

  const planningTitle = useMemo(() => {
    const vesselName = selectedVessel?.name ?? "";
    const terminalName = selectedTerminalCall?.terminal?.name ?? "";

    if (vesselName && terminalName) {
      return `${vesselName}: ${terminalName}`;
    }

    return vesselName || terminalName || t("containerPlanningPerTerminalCall");
  }, [selectedVessel, selectedTerminalCall, t]);

  const plannedDateLabel = selectedTerminalCall?.planned
    ? dayjs(selectedTerminalCall.planned).format("DD/MM/YYYY HH:mm")
    : "N/A";

  const emailRecipient =
    selectedTerminalCall?.terminal?.contacts?.[0]?.email ??
    selectedVessel?.contacts?.[0]?.email ??
    " ";

  const loadingContainers = useMemo(() => {
    return filterContainersByRefs(
      containers ?? [],
      (selectedTerminalCall as any)?.loadingContainers ?? []
    );
  }, [containers, selectedTerminalCall]);

  const unloadingContainers = useMemo(() => {
    return filterContainersByRefs(
      containers ?? [],
      (selectedTerminalCall as any)?.unloadingContainers ?? []
    );
  }, [containers, selectedTerminalCall]);

  const shifterContainers = useMemo(() => {
    return (containers ?? []).filter((container: any) => container.shiftedDate);
  }, [containers]);

  const plannedContainers = useMemo(() => {
    if (isUnloadingPlan) {
      return unloadingContainers;
    }

    if (planType === PLAN_TYPE_SHIFTERS) {
      return shifterContainers.length > 0 ? shifterContainers : containers ?? [];
    }

    return loadingContainers;
  }, [
    containers,
    isUnloadingPlan,
    loadingContainers,
    unloadingContainers,
    shifterContainers,
    planType,
  ]);

  const positionedContainers = useMemo(() => {
    return plannedContainers.filter((container: any) => container.position);
  }, [plannedContainers]);

  const totalTeu = useMemo(() => {
    return plannedContainers.reduce((total: number, container: any) => {
      return total + (container.containerType?.teu ?? 1);
    }, 0);
  }, [plannedContainers]);

  const containerColorHandler = (container: Container | undefined) => {
    if (container === undefined) {
      return NO_CONTAINER_COLOR;
    }

    if (containerColorType === LOADING_CONTAINER_COLOR) {
      return container.loadingTerminalCall &&
        container.loadingTerminalCall.terminal
        ? container.loadingTerminalCall.terminal.color
        : container.loadingTerminal && container.loadingTerminal.color
          ? container.loadingTerminal.color
          : DEFAULT_CONTAINER_COLOR;
    }

    if (containerColorType === UNLOADING_CONTAINER_COLOR) {
      const containerData = container.shifter ?? container;

      return containerData.unloadingTerminalCall &&
        containerData.unloadingTerminalCall.terminal
        ? containerData.unloadingTerminalCall.terminal.color
        : containerData.unloadingTerminal && containerData.unloadingTerminal.color
          ? containerData.unloadingTerminal.color
          : DEFAULT_CONTAINER_COLOR;
    }

    return DEFAULT_CONTAINER_COLOR;
  };

  const terminalAbbreviation = (terminal: any) =>
    terminal?.abbreviation || terminal?.shortName || terminal?.name || "—";

  const containerGridLines = (container: any) => {
    const lines: string[] = [];
    if (gridContent.containerNumber) lines.push(container.uid || "—");
    if (gridContent.weight) {
      lines.push(
        container.weight && Number(container.weight) > 0
          ? `${(Number(container.weight) / 1000).toFixed(3)} T`
          : "0 T"
      );
    }
    if (gridContent.destinationAbbreviation) {
      const data = container.shifter ?? container;
      lines.push(
        terminalAbbreviation(
          data.unloadingTerminalCall?.terminal ?? data.unloadingTerminal
        )
      );
    }
    if (gridContent.sourceAbbreviation) {
      lines.push(
        terminalAbbreviation(
          container.loadingTerminalCall?.terminal ?? container.loadingTerminal
        )
      );
    }
    return lines.slice(0, 2);
  };

  const containerGridAppearance = (container: Container | undefined) => {
    const terminalColor = containerColorHandler(container);
    if (!container) {
      return {
        backgroundColor: NO_CONTAINER_COLOR,
        color: "#334155",
        borderColor: "#dbe3ea",
      };
    }

    if (colorPreference === "border") {
      return {
        backgroundColor: "#ffffff",
        color: "#172033",
        borderColor: terminalColor,
      };
    }
    if (colorPreference === "blackWhite") {
      return {
        backgroundColor: "#1f2937",
        color: "#ffffff",
        borderColor: "#111827",
      };
    }
    if (colorPreference === "inverted") {
      return {
        backgroundColor: "#ffffff",
        color: "#111111",
        borderColor: "#111111",
      };
    }

    return {
      backgroundColor: terminalColor,
      color: textColorHandler(terminalColor),
      borderColor: terminalColor,
    };
  };

  const isContainerFromArrivalContainers = (
    container: Container | undefined
  ) => {
    if (container === undefined) {
      return false;
    }

    return (
      planType !== PLAN_TYPE_UNLOADING &&
      getTerminalCallArrivalStateContainerIds(selectedTerminalCall).includes(
        container["@id"]
      )
    );
  };

  const findContainerByPosition = (position: any[]) => {
    const positionWithVessel = [...position];

    if (selectedVessel) {
      positionWithVessel.push(normalizeId(selectedVessel["@id"]));
    }

    return plannedContainers.find((container: Container) => {
      const matchesPosition =
        container &&
        container.position &&
        JSON.stringify(container.position) ===
          JSON.stringify(positionWithVessel);

      if (!matchesPosition) {
        return false;
      }

      if (
        planType === PLAN_TYPE_LOADING &&
        container.unloadingTerminalCall &&
        container.unloadingTerminalCall["@id"] === selectedTerminalCall["@id"]
      ) {
        return false;
      }

      return true;
    });
  };

  const hasPositionBarrier = (axis: any) => {
    const requirementsObtained: any[] = [];

    if (!selectedVessel || selectedVessel.barriers?.length === 0) {
      return false;
    }

    selectedVessel.barriers?.forEach((barrierPosition: any) => {
      requirementsObtained.push(
        JSON.stringify(barrierPosition) === JSON.stringify(axis)
      );
    });

    return requirementsObtained.includes(true);
  };

  const RenderGrid = (
    layer: any,
    xAxis: any,
    yAxis: any,
    gridCount: number,
    grids: number[]
  ) => {
    const ghostGrid =
      (gridCount === 2 && (yAxis === 1 || yAxis === 4)) ||
      (gridCount === 3 && (yAxis === 1 || yAxis === 5));

    const containerWithPosition: Container | undefined =
      findContainerByPosition([layer, xAxis, yAxis]);

    const isAnArrivalContainer: boolean =
      isContainerFromArrivalContainers(containerWithPosition);

    if (isAnArrivalContainer && planType !== PLAN_TYPE_SHIFTERS) {
      return (
        <Grid
          key={`empty-arrival-${layer}-${xAxis}-${yAxis}`}
          item
          className="container-planner-grid"
        />
      );
    }

    const appearance = containerGridAppearance(containerWithPosition);
    const showEmptySymbol = Boolean(
      AVAILABLE_GRID_SYMBOLS.empty &&
        gridSymbols.empty &&
        (containerWithPosition as any)?.empty
    );
    const showAdnSymbol = Boolean(
      AVAILABLE_GRID_SYMBOLS.adn &&
        gridSymbols.adn &&
        (containerWithPosition as any)?.adn
    );
    const showReeferSymbol = Boolean(
      AVAILABLE_GRID_SYMBOLS.reefer &&
        gridSymbols.reefer &&
        (containerWithPosition as any)?.reefer
    );
    const showHeavySymbol = Boolean(
      AVAILABLE_GRID_SYMBOLS.heavy &&
        gridSymbols.heavy &&
        (containerWithPosition as any)?.heavy
    );
    const displayedSymbols: GridSymbolSetting[] = [
      showEmptySymbol && "empty",
      showAdnSymbol && "adn",
      showReeferSymbol && "reefer",
      showHeavySymbol && "heavy",
    ].filter(Boolean) as GridSymbolSetting[];
    const hasSymbol = displayedSymbols.length > 0;

    return (
      <Grid
        key={`grid-${layer}-${xAxis}-${yAxis}`}
        id={
          containerWithPosition?.["@id"]
            ? `document-container-${normalizeId(containerWithPosition["@id"])}`
            : `grid-${layer}-${xAxis}-${yAxis}`
        }
        item
        sx={{
          backgroundColor: `${appearance.backgroundColor} !important`,
          color: appearance.color,
          borderColor: `${appearance.borderColor} !important`,
          borderWidth:
            containerWithPosition && (colorPreference === "border" || colorPreference === "inverted") ? "3px !important" : undefined,
        }}
        className={`container-planner-grid ${
          containerWithPosition &&
          containerWithPosition.containerType &&
          containerWithPosition.containerType.teu > 1
            ? `container-planner-grid-teu-${containerWithPosition.containerType.teu}`
            : ""
        } ${ghostGrid ? "container-planner-ghost-grid" : ""} ${
          ghostGrid && xAxis === grids.length && gridCount === 3
            ? "container-planner-ghost-grid--small"
            : ""
        }`}
      >
        {containerWithPosition ? (
          <Grid container direction="column" justifyContent="space-between" height="100%">
            <Box sx={{ px: 0.25, pt: 0.15, minWidth: 0, overflow: "hidden" }}>
              {containerGridLines(containerWithPosition)[0] && (
                <Typography
                  className="container-planner-grid-label"
                  sx={{
                    display: "block",
                    width: "100%",
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    lineHeight: 1.1,
                    overflow: "hidden",
                    textOverflow: "clip",
                    whiteSpace: "nowrap",
                  }}
                >
                  {containerGridLines(containerWithPosition)[0]}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 0.25,
                pb: 0.15,
                minWidth: 0,
                width: "100%",
                overflow: "hidden",
                gap: 0.35,
              }}
            >
              {containerGridLines(containerWithPosition)[1] && (
                <Typography
                  className="container-planner-grid-label"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    lineHeight: 1.1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {containerGridLines(containerWithPosition)[1]}
                </Typography>
              )}
              {hasSymbol && (
                <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                  {displayedSymbols.map((symbol) => (
                    <ContainerPlanningSymbol
                      key={symbol}
                      type={symbol}
                      strokeColor={
                        colorPreference === "blackWhite" &&
                        (symbol === "empty" || symbol === "reefer" || symbol === "heavy")
                          ? "#ffffff"
                          : undefined
                      }
                      sx={{
                        fontSize: 18,
                        transform:
                          dockingSide === DOCKING_SIDE_STARBOARD
                            ? "scaleX(-1)"
                            : undefined,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        ) : (
          ""
        )}
      </Grid>
    );
  };

  return (
    <Grid container direction="column" height="100%">
      <Grid item>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            overflow: "hidden",
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{
              px: {
                xs: 2,
                md: 4,
              },
              py: {
                xs: 2,
                md: 3,
              },
            }}
          >
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    color: "primary.main",
                  }}
                >
                  <Inventory2OutlinedIcon sx={{ fontSize: 34 }} />
                </Avatar>

                <Box>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {planningTitle}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mt: 1,
                      color: "text.secondary",
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 18 }} />

                    <Typography variant="body1" color="text.secondary">
                      Planned: {plannedDateLabel}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md>
              <Stack
                direction={{
                  xs: "column",
                  md: "row",
                }}
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
              >
                <TerminalContainerPlannerButtons
                  filename={fileName()}
                  orientation={DOCUMENT_ORIENTATION_LANDSCAPE}
                  pdfFormat="a4"
                  pdfFitMode={pdfFitMode}
                  pdfMargin={5}
                  variant="card"
                  action="pdf"
                  containerRef={pdfRef}
                  terminalCall={selectedTerminalCall}
                  vessel={selectedVessel}
                  planType={planType}
                />

                <TerminalContainerPlannerButtons
                  filename={fileName()}
                  orientation={DOCUMENT_ORIENTATION_LANDSCAPE}
                  pdfFormat="a4"
                  pdfFitMode={pdfFitMode}
                  pdfMargin={5}
                  variant="card"
                  action="email"
                  containerRef={pdfRef}
                  terminalCall={selectedTerminalCall}
                  vessel={selectedVessel}
                  planType={planType}
                  emailRecipient={emailRecipient}
                />
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{
              px: {
                xs: 2,
                md: 4,
              },
              py: 1.5,
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Type:
              </Typography>

              <Typography variant="body2" fontWeight={700}>
                {planningTypeLabel}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Containers:
              </Typography>

              <Typography variant="body2" fontWeight={700}>
                {plannedContainers.length}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Positioned:
              </Typography>

              <Typography variant="body2" fontWeight={700}>
                {positionedContainers.length}/{plannedContainers.length}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {countTitle}:
              </Typography>

              <Typography variant="body2" fontWeight={700}>
                {totalTeu} TEU
              </Typography>
            </Stack>
          </Grid>
        </Paper>
      </Grid>

      <Grid item sx={{ px: { xs: 1.5, md: 3 }, pt: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.25,
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 900 }}>
              1. {t("planningDisplayFields")}{" "}
              <Box
                component="span"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                ({t("maximumTwo")})
              </Box>
            </Typography>
            <Stack sx={{ mt: 0.75 }}>
              {[
                ["containerNumber", t("containerNumber")],
                ["weight", t("weight")],
                ["destinationAbbreviation", t("destinationTerminalAbbreviation")],
                ["sourceAbbreviation", t("sourceTerminalAbbreviation")],
              ].map(([key, label]) => (
                <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
                  <Checkbox
                    size="small"
                    checked={gridContent[key as GridContentSetting]}
                    onChange={() => toggleGridContent(key as GridContentSetting)}
                    disabled={
                      !gridContent[key as GridContentSetting] &&
                      Object.values(gridContent).filter(Boolean).length >= 2
                    }
                    sx={{ p: 0.25 }}
                  />
                  <Typography sx={{ fontSize: "0.7rem" }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
            <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: "0.64rem" }}>
              {Object.values(gridContent).filter(Boolean).length} / 2{" "}
              {t("selected")}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 900 }}>
              2. {t("extraSymbolsToShow")}
            </Typography>
            <Stack sx={{ mt: 0.75 }}>
              {gridSymbolOptions.map(({ key, label }) => (
                <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
                  <Checkbox
                    size="small"
                    checked={gridSymbols[key]}
                    onChange={() => toggleGridSymbol(key)}
                    disabled={!AVAILABLE_GRID_SYMBOLS[key]}
                    sx={{ p: 0.25 }}
                  />
                  <Typography
                    sx={{
                      color: AVAILABLE_GRID_SYMBOLS[key]
                        ? "text.primary"
                        : "text.disabled",
                      fontSize: "0.7rem",
                    }}
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      ml: "auto",
                      minWidth: 24,
                      color: AVAILABLE_GRID_SYMBOLS[key]
                        ? "text.primary"
                        : "text.disabled",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ContainerPlanningSymbol
                      type={key}
                      sx={{ fontSize: 24 }}
                    />
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 900 }}>
              3. {t("colorPreferences")}
            </Typography>
            <Stack sx={{ mt: 0.75 }}>
              {[
                ["full", t("showSourceDestinationFullColorBackground")],
                ["border", t("showOnlyBorderColor")],
                ["blackWhite", t("showBlackWhiteOnly")],
                ["inverted", t("showBlackWhiteInverted")],
              ].map(([value, label]) => (
                <Stack key={value} direction="row" alignItems="center" spacing={0.5}>
                  <Radio
                    size="small"
                    checked={colorPreference === value}
                    onChange={() => setColorPreference(value as GridColorPreference)}
                    sx={{ p: 0.25 }}
                  />
                  <Typography sx={{ flex: 1, fontSize: "0.66rem" }}>{label}</Typography>
                  <Stack direction="row" spacing={0.4}>
                    {["#61c98b", "#e86d70", "#6798d0"].map((color) => (
                      <Box
                        key={color}
                        sx={{
                          width: 18,
                          height: 11,
                          bgcolor:
                            value === "full"
                              ? color
                              : value === "blackWhite"
                                ? "#222"
                                : "#fff",
                          border: "2px solid",
                          borderColor:
                            value === "border"
                              ? color
                              : value === "inverted"
                                ? "#111"
                                : "transparent",
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 900 }}>
              4. {t("rangeDisplaySettings")}
            </Typography>
            <Stack sx={{ mt: 0.75 }}>
              {[
                ["all", t("showAllLayersAlways")],
                ["filled", t("showOnlyFilledLayers")],
              ].map(([value, label]) => (
                <Stack key={value} direction="row" alignItems="center" spacing={0.5}>
                  <Radio
                    size="small"
                    checked={layerRange === value}
                    onChange={() => setLayerRange(value as GridLayerRange)}
                    sx={{ p: 0.25 }}
                  />
                  <Typography sx={{ fontSize: "0.7rem" }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Typography sx={{ fontSize: "0.78rem", fontWeight: 900 }}>
              {t("pdfPageFit")}
            </Typography>
            <Stack sx={{ mt: 0.5 }}>
              {[
                [DOCUMENT_FIT_STRETCH, t("fillWholePage")],
                [DOCUMENT_FIT_CONTAIN, t("preserveProportions")],
              ].map(([value, label]) => (
                <Stack
                  key={value}
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                >
                  <Radio
                    size="small"
                    checked={pdfFitMode === value}
                    onChange={() => setPdfFitMode(value as DocumentFitMode)}
                    sx={{ p: 0.25 }}
                  />
                  <Typography sx={{ fontSize: "0.7rem" }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>
      </Grid>

      <Grid
        item
        sx={{
          flex: 1,
          width: "100%",
          height: "100%",
          boxShadow: "none",
          border: "none",
          padding: 1,
          backgroundColor: "transparent",
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Box
          ref={pdfRef}
          className="pdf-document-content"
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 1800,
            minWidth: 1000,
            height: "auto",
            minHeight: 900,
            px: 0,
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              border: "1px solid #d0d5dd",
              borderRadius: "4px 4px 0 0",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                backgroundColor: "#1e293b",
                color: "#fff",
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.02em" }}>
                {selectedVessel?.name}
                {selectedTerminalCall?.terminal?.name
                  ? ` — ${selectedTerminalCall.terminal.name}`
                  : ""}
              </Typography>
            </Box>

            <Box sx={{ px: 1.5, py: 0.75, fontSize: "0.72rem", color: "#334155" }}>
              <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 1.5, alignItems: "stretch" }}>
                <Box sx={{ flex: 1, minWidth: 0, backgroundColor: "#f8fafc", borderRadius: 1, px: 1.25, py: 0.75, border: "1px solid #e2e8f0" }}>
                  <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("destination")}</Box>
                  <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{selectedTerminalCall?.terminal?.city || selectedTerminalCall?.terminal?.name || "\u2014"}</Box>
                  <Box sx={{ mt: 0.75, color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("type")}</Box>
                  <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{planningTypeLabel}</Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, backgroundColor: "#f8fafc", borderRadius: 1, px: 1.25, py: 0.75, border: "1px solid #e2e8f0" }}>
                  <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("planned")}</Box>
                  <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{dayjs(selectedTerminalCall?.planned).format("DD/MM/YYYY HH:mm")}</Box>
                  <Box sx={{ mt: 0.75, display: "flex", gap: 1.5 }}>
                    {selectedTerminalCall?.arrival && (
                      <Box>
                        <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("arrival")}</Box>
                        <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{dayjs(selectedTerminalCall.arrival).format("DD/MM/YYYY HH:mm")}</Box>
                      </Box>
                    )}
                    {selectedTerminalCall?.departure && (
                      <Box>
                        <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("departure")}</Box>
                        <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{dayjs(selectedTerminalCall.departure).format("DD/MM/YYYY HH:mm")}</Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, backgroundColor: "#f8fafc", borderRadius: 1, px: 1.25, py: 0.75, border: "1px solid #e2e8f0", display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, max-content)", gap: "0.4rem 1rem", fontSize: "0.72rem", color: "#64748b" }}>
                    {gridSymbolOptions.map(({ key, label }) => (
                      <Box
                        key={key}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <ContainerPlanningSymbol
                          type={key}
                          sx={{ fontSize: 22, flexShrink: 0 }}
                        />
                        {label}
                      </Box>
                    ))}
                  </Box>
                </Box>
                {planType !== PLAN_TYPE_SHIFTERS && (
                  <Box sx={{ flex: 1, minWidth: 0, backgroundColor: "#f8fafc", borderRadius: 1, px: 1.25, py: 0.75, border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                      <Box>
                        <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("containers")}</Box>
                        <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{plannedContainers.length}</Box>
                      </Box>
                      <Box>
                        <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{countTitle}</Box>
                        <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{totalTeu} TEU</Box>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: "0.5rem 1rem" }}>
                      {(containerTypes?.data ?? containerTypes ?? []).map((ct: any) => {
                        const count = plannedContainers.filter((c: any) => (c.containerType["@id"] ?? c.containerType.id) === (ct["@id"] ?? ct.id)).length;
                        return count > 0 ? (
                          <Box key={ct["@id"] ?? ct.id}>
                            <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{ct.abbreviation ?? ct.name}</Box>
                            <Box sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{count}</Box>
                          </Box>
                        ) : null;
                      })}
                    </Box>
                  </Box>
                )}
                {(() => {
                  const NO_CONTAINER_COLOR = "#FFFFFF";
                  const DEFAULT_CONTAINER_COLOR = "#FFE4C4";
                  const containerColorFor = (container) => {
                    if (!container) return NO_CONTAINER_COLOR;
                    if (containerColorType === "loading_color") {
                      return container.loadingTerminalCall?.terminal?.color ?? container.loadingTerminal?.color ?? DEFAULT_CONTAINER_COLOR;
                    }
                    const c = container.shifter ?? container;
                    return c.unloadingTerminalCall?.terminal?.color ?? c.unloadingTerminal?.color ?? DEFAULT_CONTAINER_COLOR;
                  };
                  const terminalOf = (container) => {
                    if (!container) return undefined;
                    if (containerColorType === "loading_color") {
                      return container.loadingTerminalCall?.terminal ?? container.loadingTerminal;
                    }
                    const c = container.shifter ?? container;
                    return c.unloadingTerminalCall?.terminal ?? c.unloadingTerminal;
                  };
                  const buckets = new Map();
                  for (const c of plannedContainers) {
                    const term = terminalOf(c);
                    if (!term?.name) continue;
                    const key = term["@id"] ?? term.name;
                    const existing = buckets.get(key);
                    if (existing) {
                      existing.count += 1;
                    } else {
                      buckets.set(key, { name: term.name, color: containerColorFor(c), count: 1 });
                    }
                  }
                  const items = Array.from(buckets.values());
                  const mid = Math.ceil(items.length / 2);
                  const row1 = items.slice(0, mid);
                  const row2 = items.slice(mid);
                  return items.length > 0 ? (
                    <Box sx={{ flex: 1, minWidth: 0, backgroundColor: "#f8fafc", borderRadius: 1, px: 1.25, py: 0.75, border: "1px solid #e2e8f0" }}>
                      <Box sx={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {containerColorType === "loading_color" ? t("origin") : t("destination")}
                      </Box>
                      {[row1, row2].map((row, ri) => (
                        <Box key={ri} sx={{ display: "flex", flexWrap: "wrap", gap: "0.25rem 0.75rem", mt: ri === 0 ? 0.5 : 0.25 }}>
                          {row.map((item) => (
                            <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 0.35, whiteSpace: "nowrap", fontSize: "0.68rem" }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: item.color, flexShrink: 0 }} />
                              {item.name} ({item.count})
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  ) : null;
                })()}
              </Box>
              {Array.isArray(selectedVessel?.contacts) && selectedVessel.contacts.length > 0 && (
                <Box sx={{ color: "#64748b", mt: 0.5, fontSize: "0.7rem" }}>
                  {selectedVessel.contacts.map((contact: any, idx: number) => (
                    <span key={idx}>
                      {idx > 0 && " | "}
                      {contact.name && <strong>{contact.name}</strong>}
                      {contact.phoneNumber && <> Tel.: {contact.phoneNumber}</>}
                      {contact.email && <> {contact.email}</>}
                    </span>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          <style>{`
            .pdf-document-content > .container-planner-main-wrapper {
              height: auto !important;
              min-height: 200px;
              overflow: visible !important;
              border: none !important;
            }
            .pdf-document-content > .container-planner-main-wrapper > .container-planner-wrapper {
              width: 100% !important;
              margin-right: 0 !important;
              padding: 8px 0 !important;
            }
            .pdf-document-content > .container-planner-main-wrapper .container-planner-ship-body {
              display: flex !important;
              width: 100% !important;
              min-width: 0;
              gap: 0;
            }
            .pdf-document-content > .container-planner-main-wrapper .container-planner-ship-body > .container-planner-ship-grid-wrapper {
              flex: 1 1 0px !important;
              min-width: 0 !important;
              max-width: none !important;
              overflow: visible;
            }
            .pdf-document-content > .container-planner-main-wrapper .container-planner-ship-grid-wrapper > .container-planner-grid {
              width: calc(100% - 2px) !important;
              margin: 1px !important;
              border-width: 1px !important;
            }
            .pdf-document-content > .container-planner-main-wrapper .container-planner-ship-grid-wrapper > .container-planner-grid-teu-2 {
              width: calc(200% - 2px) !important;
            }
            .pdf-document-content > .container-planner-main-wrapper .container-planner-ship-grid-wrapper > .container-planner-grid-teu-3 {
              width: calc(300% - 2px) !important;
            }
            .pdf-document-content > .container-planner-main-wrapper .container-planner-ship-bow {
              --bow-width: 32px !important;
              background-color: #1976D2 !important;
            }
            .pdf-document-content .container-planner-grid .container-planner-grid-label {
              font-size: 12px !important;
              line-height: 1.1 !important;
              white-space: nowrap;
            }
            .pdf-document-content > .container-planner-main-wrapper .container-planner-ship-bow::before {
              content: none !important;
            }
          `}</style>

          <Box
            className="container-planner-main-wrapper"
            sx={{
              width: "100%",
              px: 0.5,
              py: 0.5,
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
            }}
          >
              {selectedVessel &&
                selectedTerminalCall &&
                selectedVessel.positions &&
                selectedVessel.positions
                  .map((grids: any, layer: number) => ({ grids, layer }))
                  .filter(
                    ({ layer }: { layer: number }) =>
                      layer !== 4 &&
                      (layerRange === "all" ||
                        plannedContainers.some(
                          (container: any) =>
                            Array.isArray(container?.position) &&
                            Number(container.position[0]) === layer + 1
                        ))
                  )
                  .map(({ grids, layer }: { grids: any; layer: number }) => (
                  <Grid
                    className={`container-planner-wrapper ${
                      dockingSide === DOCKING_SIDE_STARBOARD
                        ? "container-planner-wrapper--revese"
                        : ""
                    }`}
                    item
                    key={`vessel-layer-${layer}`}
                  >
                    <Grid className="container-planner-ship-bow" item>
                      <Typography className="container-planner-layer-label">
                        {layer + 1}
                      </Typography>
                    </Grid>

                    <Grid
                      className="container-planner-ship-body"
                      item
                    >
                      {grids &&
                        grids.map((gridCount: number, x: number) => (
                          <React.Fragment key={`grid-column-${x}`}>
                            {planType === PLAN_TYPE_SHIFTERS &&
                              plannedContainers.map((container: any) =>
                                container.shifterOriginal ? (
                                  <SimpleShifterArrowConnector
                                    key={container["@id"]}
                                    fromId={`document-container-${normalizeId(
                                      container.shifterOriginal["@id"]
                                    )}`}
                                    toId={`document-container-${normalizeId(
                                      container["@id"]
                                    )}`}
                                    containerRef={pdfRef}
                                  />
                                ) : null
                              )}

                            <Grid
                              className="container-planner-ship-grid-wrapper"
                              item
                            >
                              <Typography className="container-planner-x-axis-label">
                                {x + 1}
                              </Typography>

                              {[...Array(gridCount)].map((_, y) =>
                                RenderGrid(
                                  layer + 1,
                                  x + 1,
                                  y + 1,
                                  gridCount,
                                  grids
                                )
                              )}
                            </Grid>

                            {hasPositionBarrier([x + 1, x + 2]) && (
                              <Grid
                                className="container-planner-barrier"
                                item
                              />
                            )}
                          </React.Fragment>
                        ))}
                    </Grid>
                  </Grid>
                ))}

              {!selectedVessel?.positions?.length && (
                <Grid item sx={{ display: "flex", justifyContent: "center" }}>
                  <Alert severity="warning">
                    {t("noVesselGridPosition")}
                  </Alert>
                </Grid>
              )}
            </Box>
          </Box>
      </Grid>
    </Grid>
  );
};

export default TerminalCallContainerPlanning;
